import { useMemo } from 'react'
import { useQuery } from 'react-query'
import { format } from 'date-fns'
import {
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon
} from '@heroicons/react/24/outline'

import { query } from '../api'
import Table from '../components/Table'
import Loader from '../components/Loader'
import { aggregate } from '../helper/assistant-questions-aggregate'
import {
  frageOverlap,
  sharedSignificantWords
} from '../helper/question-overlap'

const QUESTION_LOG_MARKER = 'Q'
const AI_INPUT_TOKENS = 'ai_input_tokens'
const AI_OUTPUT_TOKENS = 'ai_output_tokens'

// Claude Sonnet 5 über Bedrock, EU-Regional-Endpoint (eu.anthropic.claude-sonnet-5,
// siehe config.js im Backend — gewechselt von Opus 5 am 01.09.2026, die
// Aufgabe ist reines RAG statt komplexes Reasoning): $2/$10 pro Mio. Token
// global, +10% Regional-Aufschlag (platform.claude.com/docs/en/about-claude/
// pricing, Stand 09/2026) = $2.20/$11. Reine Schätzung aus Token-Zahlen zur
// aktuellen Listenpreis-Rate — keine echte Abrechnung (kein Rabatt/Prompt-
// Caching berücksichtigt). Bei Modellwechsel oder Preisänderung hier
// nachziehen.
const USD_PER_MTOK_INPUT = 2.2
const USD_PER_MTOK_OUTPUT = 11

const estimateUsd = (inputTokens, outputTokens) =>
  ((inputTokens || 0) / 1_000_000) * USD_PER_MTOK_INPUT +
  ((outputTokens || 0) / 1_000_000) * USD_PER_MTOK_OUTPUT

const formatUsd = (value) =>
  `$${value.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`

/** '2026-08' — muss mit usageMeter.monthlyPeriod() im Backend übereinstimmen. */
const currentPeriod = () => new Date().toISOString().slice(0, 7)

// Bündelt Fragen, die laut frageOverlap dasselbe Anliegen sind — Union-Find
// über eine Kante "overlap >= 1" (dieselbe Schwelle wie selectTopics im
// Backend). Transitiv: A–B und B–C teilen sich je ein Wort, landen also
// gemeinsam in einem Cluster, auch ohne dass A und C direkt ein Wort teilen.
const clusterByOverlap = (rankingItems) => {
  const parent = rankingItems.map((_, i) => i)
  const find = (i) => {
    while (parent[i] !== i) {
      parent[i] = parent[parent[i]]
      i = parent[i]
    }
    return i
  }
  const union = (a, b) => {
    const ra = find(a)
    const rb = find(b)
    if (ra !== rb) parent[ra] = rb
  }

  for (let i = 0; i < rankingItems.length; i += 1) {
    for (let j = i + 1; j < rankingItems.length; j += 1) {
      if (frageOverlap(rankingItems[i].frage, rankingItems[j].frage) >= 1)
        union(i, j)
    }
  }

  const groups = new Map()
  rankingItems.forEach((item, i) => {
    const root = find(i)
    if (!groups.has(root)) groups.set(root, [])
    groups.get(root).push(item)
  })

  return Array.from(groups.values())
    .map((items) => {
      const sorted = [...items].sort((a, b) => b.count - a.count)
      const texte = items.map((i) => i.beispiel)
      return {
        beispiel: sorted[0].beispiel,
        count: items.reduce((sum, i) => sum + i.count, 0),
        varianten: sorted.slice(1).map((i) => i.beispiel),
        // Nur relevant (und nur berechnet), wenn es ueberhaupt Varianten
        // gibt — bei einer Einzelfrage waere jedes eigene Wort "gemeinsam".
        gemeinsameWoerter:
          sorted.length > 1 ? sharedSignificantWords(texte) : []
      }
    })
    .sort((a, b) => b.count - a.count)
}

// Baut ein Regex, das jedes Wort case-insensitiv trifft — plus gaengige
// Umlaut-Varianten, weil significantWords() intern NFD-normalisiert
// (Diakritika entfernt) und der Originaltext sie noch traegt ("Für" vs "fuer").
const DIACRITIC_CLASS = {
  a: '[aàáâä]',
  e: '[eèéêë]',
  i: '[iìíîï]',
  o: '[oòóôö]',
  u: '[uùúûü]',
  s: '[sß]',
  n: '[nñ]'
}

const buildHighlightRegex = (words) => {
  if (!words || words.length === 0) return null
  const alternatives = Array.from(new Set(words))
    .sort((a, b) => b.length - a.length)
    .map((word) =>
      word
        .replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
        .split('')
        .map((ch) => DIACRITIC_CLASS[ch] || ch)
        .join('')
    )
  return new RegExp(`(${alternatives.join('|')})`, 'gi')
}

/** Hebt die gemeinsamen Cluster-Wörter farbig hervor — zeigt, WARUM zwei Fragen als gleich gelten. */
const HighlightedText = ({ text, words }) => {
  const pattern = buildHighlightRegex(words)
  if (!pattern || typeof text !== 'string') return <>{text}</>

  const parts = text.split(pattern)
  return (
    <>
      {parts.map((part, index) =>
        index % 2 === 1 ? (
          // eslint-disable-next-line react/no-array-index-key
          <mark
            key={index}
            className='rounded bg-amber-200 px-0.5 text-darkBlack'
          >
            {part}
          </mark>
        ) : (
          // eslint-disable-next-line react/no-array-index-key
          <span key={index}>{part}</span>
        )
      )}
    </>
  )
}

const parseQuestionItems = (items) =>
  (items || []).map((item) => {
    let data = null
    try {
      data = typeof item.data === 'string' ? JSON.parse(item.data) : item.data
    } catch {
      data = null
    }
    return { ...item, event_name: 'ASSISTANT_QUESTION', data }
  })

const formatDate = (iso) => (iso ? format(new Date(iso), 'dd.MM.yyyy') : '—')

const GRUND_LABEL = {
  no_content: 'Inhaltslücke — Auftrag für die Wissensbasis',
  unknown_frontend: 'Unbekanntes Frontend — Konfigurationsfehler'
}

const Assistant = () => {
  const { data: questionItems, isLoading: isLoadingQuestions } = useQuery({
    queryKey: ['assistant-questions'],
    queryFn: () =>
      query({
        query: 'assistantQuestionsLog',
        variables: { question_log_marker: QUESTION_LOG_MARKER }
      })
  })

  const { data: usageCounters, isLoading: isLoadingUsage } = useQuery({
    queryKey: ['assistant-usage-counters'],
    queryFn: () => query({ query: 'listUsageCounters' })
  })

  const { data: partners, isLoading: isLoadingPartners } = useQuery({
    queryKey: ['assistant-partners'],
    queryFn: () => query({ query: 'listPartners' })
  })

  const isLoading = isLoadingQuestions || isLoadingUsage || isLoadingPartners

  const questions = useMemo(
    () => parseQuestionItems(questionItems),
    [questionItems]
  )

  const stats = useMemo(() => aggregate(questions), [questions])

  const unansweredRanking = useMemo(
    () =>
      aggregate(questions.filter((q) => q.data?.knowledge_source === 'none'))
        .nachFrage,
    [questions]
  )

  // Buendelt Formulierungen desselben Anliegens, bevor sie als Ranking
  // gezeigt werden ("Was ist eine Testbatterie?" / "...und wofuer braucht
  // man die?") — sonst verwaessert die exakte Normalform aus aggregate()
  // die Prioritaet unnoetig auf mehrere Zeilen.
  const unansweredClusters = useMemo(
    () => clusterByOverlap(unansweredRanking),
    [unansweredRanking]
  )

  const partnerNameById = useMemo(() => {
    const map = new Map()
    ;(partners || []).forEach((p) => map.set(p.id, p.display_name || p.name))
    return map
  }, [partners])

  const tokensThisMonth = useMemo(() => {
    const period = currentPeriod()
    const rows = (usageCounters || []).filter((c) => c.period === period)
    const input = rows
      .filter((c) => c.metric === AI_INPUT_TOKENS)
      .reduce((sum, c) => sum + (c.count || 0), 0)
    const output = rows
      .filter((c) => c.metric === AI_OUTPUT_TOKENS)
      .reduce((sum, c) => sum + (c.count || 0), 0)
    return { input, output, costUsd: estimateUsd(input, output) }
  }, [usageCounters])

  // Nutzung je Kunde: braucht eine eigene Gruppierung nach partner_id —
  // aggregate() rechnet global, nicht partnerweise. Nur Partner mit
  // Aktivität, sonst wäre die Tabelle bei 2637 Partnern unlesbar.
  const usageByPartner = useMemo(() => {
    const period = currentPeriod()
    const byPartner = new Map()

    const entry = (partnerId) => {
      if (!byPartner.has(partnerId)) {
        byPartner.set(partnerId, {
          partner_id: partnerId,
          partner_name: partnerNameById.get(partnerId) || partnerId,
          fragen_gesamt: 0,
          fragen_ohne_wissen: 0,
          input_tokens: 0,
          output_tokens: 0
        })
      }
      return byPartner.get(partnerId)
    }

    questions.forEach((q) => {
      if (!q.partner_id) return
      const row = entry(q.partner_id)
      row.fragen_gesamt += 1
      if (q.data?.knowledge_source === 'none') row.fragen_ohne_wissen += 1
    })
    ;(usageCounters || [])
      .filter((c) => c.period === period)
      .forEach((c) => {
        if (!c.partner_id) return
        const row = entry(c.partner_id)
        if (c.metric === AI_INPUT_TOKENS) row.input_tokens += c.count || 0
        if (c.metric === AI_OUTPUT_TOKENS) row.output_tokens += c.count || 0
      })

    return Array.from(byPartner.values())
      .map((row) => ({
        ...row,
        geschaetzte_kosten_usd: estimateUsd(row.input_tokens, row.output_tokens)
      }))
      .sort(
        (a, b) =>
          b.fragen_gesamt +
          b.input_tokens +
          b.output_tokens -
          (a.fragen_gesamt + a.input_tokens + a.output_tokens)
      )
  }, [questions, usageCounters, partnerNameById])

  const kpis = [
    { name: 'Fragen gesamt', stat: stats.gesamt },
    {
      name: 'Zeitraum',
      stat: stats.zeitraum
        ? `${formatDate(stats.zeitraum.von)} – ${formatDate(stats.zeitraum.bis)}`
        : '—'
    },
    {
      name: 'Anteil ohne Wissen',
      stat:
        stats.gesamt > 0
          ? `${Math.round((unansweredRanking.reduce((s, r) => s + r.count, 0) / stats.gesamt) * 100)}%`
          : '—'
    },
    {
      name: 'Mit Entität (@career/@space)',
      stat:
        stats.gesamt > 0
          ? `${stats.mitEntitaet} (${Math.round((stats.mitEntitaet / stats.gesamt) * 100)}%)`
          : '—'
    },
    { name: 'Input-Tokens (dieser Monat)', stat: tokensThisMonth.input },
    { name: 'Output-Tokens (dieser Monat)', stat: tokensThisMonth.output },
    {
      name: 'Geschätzte Kosten (dieser Monat)',
      stat: formatUsd(tokensThisMonth.costUsd)
    }
  ]

  const usageColumns = [
    { header: 'KUNDE', accessorKey: 'partner_name' },
    { header: 'FRAGEN GESAMT', accessorKey: 'fragen_gesamt' },
    { header: 'DAVON OHNE WISSEN', accessorKey: 'fragen_ohne_wissen' },
    { header: 'INPUT-TOKENS', accessorKey: 'input_tokens' },
    { header: 'OUTPUT-TOKENS', accessorKey: 'output_tokens' },
    {
      header: 'GESCHÄTZTE KOSTEN',
      accessorKey: 'geschaetzte_kosten_usd',
      cell: (props) => <span>{formatUsd(props.getValue())}</span>
    }
  ]

  const rankingColumns = [
    {
      header: 'FRAGE',
      accessorKey: 'beispiel',
      cell: (props) => {
        const row = props.row.original
        return (
          <div>
            <HighlightedText
              text={row.beispiel}
              words={row.gemeinsameWoerter}
            />
            {row.varianten.length > 0 && (
              <ul className='mt-1 space-y-0.5'>
                {row.varianten.map((variante) => (
                  <li key={variante} className='text-xs text-mediumGrey'>
                    <HighlightedText
                      text={variante}
                      words={row.gemeinsameWoerter}
                    />
                  </li>
                ))}
              </ul>
            )}
          </div>
        )
      }
    },
    { header: 'ANZAHL', accessorKey: 'count' }
  ]

  if (isLoading) {
    return <Loader loadingText={'Assistenten-Daten werden geladen ...'} />
  }

  return (
    <div>
      <div className='sm:flex sm:items-center'>
        <div className='sm:flex-auto'>
          <div className='flex items-center gap-3'>
            <span className='inline-flex rounded-lg bg-paleWhiteBlue p-2 text-primaryBlue'>
              <ChatBubbleLeftRightIcon className='h-6 w-6' />
            </span>
            <h1 className='text-base font-semibold leading-6 text-darkBlack'>
              Assistent
            </h1>
          </div>
          <p className='mt-2 text-sm text-rockGrey'>
            Token-Verbrauch und Fragen des Dashboard-Assistenten — nach Kunde
            und nach Häufigkeit.
          </p>
          <p className='mt-1 text-xs text-mediumGrey'>
            Kosten sind eine Schätzung zum aktuellen Listenpreis von Claude
            Sonnet 5 (EU-Regional-Endpoint) — kein Rabatt, kein Prompt-Caching,
            keine echte AWS-Abrechnung.
          </p>
        </div>
      </div>

      <div className='mt-8'>
        <dl className='grid grid-cols-1 gap-5 sm:grid-cols-3 lg:grid-cols-6'>
          {kpis.map((item) => (
            <div
              key={item.name}
              className='overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:p-6'
            >
              <dt className='truncate text-sm font-medium text-gray-500'>
                {item.name}
              </dt>
              <dd className='mt-1 text-2xl font-semibold tracking-tight text-gray-900'>
                {item.stat}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {stats.gesamt === 0 ? (
        <div className='mt-8 rounded-2xl bg-backgroundGrey p-6 border border-softGrey flex flex-col items-center justify-center gap-2 py-16 text-center'>
          <ChatBubbleLeftRightIcon className='h-8 w-8 text-softGrey' />
          <p className='text-sm font-medium text-darkBlack'>
            Noch keine Fragen protokolliert
          </p>
          <p className='text-xs text-mediumGrey'>
            Sobald der Assistent im Kunden-Dashboard genutzt wird, erscheinen
            hier die Zahlen.
          </p>
        </div>
      ) : (
        <>
          <div className='mt-8'>
            <h2 className='text-sm font-semibold text-darkBlack'>
              Nutzung je Kunde
            </h2>
            <div className='mt-2 flow-root'>
              <div className='-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
                <div className='inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8'>
                  <Table data={usageByPartner} columns={usageColumns} />
                </div>
              </div>
            </div>
          </div>

          <div className='mt-10'>
            <div className='flex items-center gap-2'>
              <QuestionMarkCircleIcon className='h-5 w-5 text-primaryBlue' />
              <h2 className='text-sm font-semibold text-darkBlack'>
                Was als Nächstes beantwortet werden sollte
              </h2>
            </div>
            <p className='mt-1 text-xs text-mediumGrey'>
              Unbeantwortete Fragen (`knowledge_source: none`), sortiert nach
              Häufigkeit — die Priorisierung für die Wissensbasis-Arbeit.
            </p>
            <div className='mt-2 flow-root'>
              <div className='-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8'>
                <div className='inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8'>
                  {unansweredClusters.length > 0 ? (
                    <Table data={unansweredClusters} columns={rankingColumns} />
                  ) : (
                    <p className='text-sm text-mediumGrey'>
                      Aktuell keine unbeantworteten Fragen.
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className='mt-10 grid grid-cols-1 gap-6 lg:grid-cols-3'>
            <div className='rounded-2xl bg-backgroundGrey p-6 border border-softGrey'>
              <h3 className='text-sm font-semibold text-darkBlack'>
                Warum unbeantwortet
              </h3>
              <dl className='mt-3 space-y-2'>
                {stats.ohneWissen.length === 0 && (
                  <p className='text-xs text-mediumGrey'>—</p>
                )}
                {stats.ohneWissen.map(({ grund, count }) => (
                  <div
                    key={grund}
                    className='flex items-center justify-between gap-3'
                  >
                    <dt className='text-xs text-mediumGrey'>
                      {GRUND_LABEL[grund] || grund}
                    </dt>
                    <dd className='text-sm font-semibold text-darkBlack'>
                      {count}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className='rounded-2xl bg-backgroundGrey p-6 border border-softGrey'>
              <h3 className='text-sm font-semibold text-darkBlack'>
                Nach Screen
              </h3>
              <dl className='mt-3 space-y-2'>
                {stats.nachScreen.map(({ screen, count }) => (
                  <div
                    key={screen}
                    className='flex items-center justify-between gap-3'
                  >
                    <dt className='text-xs text-mediumGrey'>{screen}</dt>
                    <dd className='text-sm font-semibold text-darkBlack'>
                      {count}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className='rounded-2xl bg-backgroundGrey p-6 border border-softGrey'>
              <h3 className='text-sm font-semibold text-darkBlack'>
                Nach Rolle
              </h3>
              <dl className='mt-3 space-y-2'>
                {stats.nachRolle.map(({ role, count }) => (
                  <div
                    key={role}
                    className='flex items-center justify-between gap-3'
                  >
                    <dt className='text-xs text-mediumGrey'>{role}</dt>
                    <dd className='text-sm font-semibold text-darkBlack'>
                      {count}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Assistant
